// Facebook (Meta) transforms that shape a `launcherResult` — the campaign
// config the campaign launcher writes to parsed_response.json — into the
// objects the shared CLW review grid + summary pane consume. Pure data — no
// React, no I/O. Mirrors gads/gadsRowBuilders.js and ttd/ttdRowBuilders.js so
// callers dispatch by platform without per-platform JSX branching.
//
// Real payload shape (campaign-services/fb_api/ex_facebook_launch.json, read by
// fb_api/fb_campaign_preprocessor.py → fb_campaign_validator.py →
// fb_campaign_launcher.py):
//
//   { campaign: { campaign_name, goal, budget, daily_budget, lifetime_budget,
//                 start_date, end_date, bid_strategy, attribution_setting,
//                 status },
//     ad_sets: [{ package_name, ad_set_suffix, name, budget, daily_budget,
//                 lifetime_budget, start_date, end_date, bid_strategy,
//                 bid_amount, optimization_goal, billing_event, tactic, status,
//                 targeting: { geo_locations: { countries, regions, cities,
//                                zips, geo_markets, electoral_districts },
//                              age_min, age_max, genders, interests,
//                              custom_audiences, excluded_custom_audiences,
//                              facebook_positions, publisher_platforms,
//                              device_platforms, locales,
//                              excluded_geo_locations },
//                 targeting_profiles: ['profile_key'],
//                 Creatives | creatives: [{ creative_name, format, ad_copy,
//                                           asset_url, landing_page }] }],
//     targeting_profiles: [{ targeting_profile_key, …same targeting shape }],
//     creatives: [{ … }] }
//
// BUDGETS ARE IN WHOLE CURRENCY UNITS HERE, often as strings ("40000"). The
// cents conversion is the PREPROCESSOR's job at launch
// (fb_campaign_preprocessor._convert_budget multiplies by 100), so this module
// must not convert — it reads dollars and writes dollars, and fbEdits does the
// same.
//
// The two ad-set key spellings both appear in the wild: the launch template
// states 'Ad Sets' (and 'Creatives'), the preprocessor normalises to 'ad_sets'
// (FIELD_NAME_MAPPINGS). Every reader below accepts both, so a plan renders
// whichever side of that normalisation it was captured on.

import { fmtCurrency } from '../clwFormatters';
import { FB_FMT, FB_PLATFORM_LABEL, isFbPlatform } from './fbConstants';

const DASH = '—';

const asArray = (v) => (Array.isArray(v) ? v : []);
const nonEmpty = (v) => (v == null ? null : (String(v).trim() || null));

/**
 * Money as stated by the plan. Values arrive as numbers or as formatted
 * strings ("$40,000" / "40000"), so strip currency formatting before Number().
 * Returns null for anything that isn't a finite number, which is what makes a
 * missing budget render '—' instead of $0.
 */
export function fbMoney(value) {
  if (value == null || value === '') return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  const cleaned = String(value).replace(/[^0-9.-]/g, '');
  if (!cleaned || cleaned === '-' || cleaned === '.') return null;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
}

/**
 * Normalise the date spellings the Facebook templates carry to YYYY-MM-DD:
 * '2/15/2025' (the launch template's own format), '2025-02-15' and the
 * preprocessor's timezone-stamped '2025-02-15T00:00:00-0800'. Anything else
 * passes through trimmed, so the grid shows what the plan actually says.
 */
export function fbYmd(value) {
  const s = nonEmpty(value);
  if (!s) return null;
  const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;
  const us = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(s);
  if (us) return `${us[3]}-${us[1].padStart(2, '0')}-${us[2].padStart(2, '0')}`;
  return s;
}

/** The campaign envelope; `{}` when the plan states none. */
export const fbCampaignOf = (result) => (
  result && typeof result.campaign === 'object' && !Array.isArray(result.campaign)
    ? result.campaign
    : {}
);

/** Ad sets, under either spelling. */
export const fbAdSetsOf = (result) => {
  for (const key of ['ad_sets', 'Ad Sets', 'adSets', 'AdSets', 'adsets']) {
    const list = asArray(result?.[key]);
    if (list.length) return list;
  }
  return [];
};

/** The key `result` actually stores its ad sets under — fbEdits writes there. */
export const fbAdSetsKey = (result) => {
  for (const key of ['ad_sets', 'Ad Sets', 'adSets', 'AdSets', 'adsets']) {
    if (Array.isArray(result?.[key])) return key;
  }
  return 'ad_sets';
};

/** One ad set's creatives, under either spelling. */
export const fbCreativesOf = (adSet) => {
  for (const key of ['Creatives', 'creatives', 'ads', 'Ads']) {
    const list = asArray(adSet?.[key]);
    if (list.length) return list;
  }
  return [];
};

// `· `-joined label + count summary for a list cell — the convention every
// other platform's builders use (grid cell shows a count, the detail panel
// expands the full list).
const listSummary = (items, noun) => {
  const list = asArray(items).map((v) => nonEmpty(v)).filter(Boolean);
  if (!list.length) return { label: DASH, list: DASH, count: 0 };
  const plural = list.length === 1 ? noun : `${noun}s`;
  return { label: `${list.length} ${plural}`, list: list.join(' · '), count: list.length };
};

// Targeting entries are strings or {id, name} objects (Meta's own shape) —
// render the name, falling back to the id so an id-only entry is still visible.
const targetingLabel = (v) => (v && typeof v === 'object'
  ? nonEmpty(v.name ?? v.key ?? v.id)
  : nonEmpty(v));

const labelList = (items) => asArray(items).map(targetingLabel).filter(Boolean);

/**
 * The targeting an ad set effectively runs on: its own inline `targeting`
 * merged over any `targeting_profiles` it references by key. That indirection
 * is real — fb_campaign_validator resolves geo through it, and a plan that
 * states geo ONLY in a profile would otherwise render an empty geo cell.
 *
 * Inline keys win: the validator treats inline targeting as the ad set's own
 * and the profiles as a shared base.
 */
export function fbTargetingOf(adSet, result) {
  const profilesByKey = new Map();
  for (const p of asArray(result?.targeting_profiles)) {
    const key = nonEmpty(p?.targeting_profile_key);
    if (key && !profilesByKey.has(key)) profilesByKey.set(key, p);
  }
  const merged = {};
  for (const ref of asArray(adSet?.targeting_profiles)) {
    const prof = (ref && typeof ref === 'object') ? ref : profilesByKey.get(nonEmpty(ref));
    if (prof && typeof prof === 'object') Object.assign(merged, prof);
  }
  const inline = adSet?.targeting;
  if (inline && typeof inline === 'object') Object.assign(merged, inline);
  return merged;
}

// Geo containers, in the order a reviewer reads them. `geo_markets` are DMAs
// ({key: 'DMA:501', name}) and `zips` are 'US:94002' strings or {key} objects.
const GEO_KEYS = ['countries', 'country_groups', 'regions', 'cities', 'geo_markets', 'electoral_districts'];

// Country codes render as-is; every other container renders its name, and a
// DMA with no name falls back to its key so the cell is never blank.
function geoLabels(container) {
  if (!container || typeof container !== 'object') return [];
  const out = [];
  for (const key of GEO_KEYS) {
    for (const v of asArray(container[key])) {
      const label = (v && typeof v === 'object')
        ? nonEmpty(v.name ?? v.key ?? v.id)
        : nonEmpty(v);
      if (label) out.push(label);
    }
  }
  return out;
}

// Zip codes get their own cell — a plan can carry hundreds, and mixing them
// into the geo cell buries the market list they were meant to refine.
function zipLabels(container) {
  if (!container || typeof container !== 'object') return [];
  const out = [];
  for (const key of ['zips', 'zipcodes']) {
    for (const v of asArray(container[key])) {
      const label = (v && typeof v === 'object') ? nonEmpty(v.key ?? v.name) : nonEmpty(v);
      if (label) out.push(label);
    }
  }
  return out;
}

function resolveFbGeo(targeting) {
  const include = geoLabels(targeting?.geo_locations || targeting?.geo);
  const exclude = geoLabels(targeting?.excluded_geo_locations);
  const zips = zipLabels(targeting?.geo_locations || targeting?.geo);
  const geo = include.length
    ? (exclude.length ? `${include.join(' · ')} · excl. ${exclude.length}` : include.join(' · '))
    // A ZIP-only plan is a real shape (the validator accepts zips as the sole
    // location type), so fall through to a zip count rather than showing '—'.
    : (zips.length ? `${zips.length} zip${zips.length === 1 ? '' : 's'}` : DASH);
  return {
    geo,
    geoIncludeList: include.length ? include.join(' · ') : DASH,
    geoExcludeList: exclude.length ? exclude.join(' · ') : DASH,
    zips,
  };
}

// Age cell: "25–54", or a one-sided bound when the plan states only one.
function resolveAge(targeting) {
  const min = Number(targeting?.age_min);
  const max = Number(targeting?.age_max);
  const hasMin = Number.isFinite(min) && min > 0;
  const hasMax = Number.isFinite(max) && max > 0;
  if (hasMin && hasMax) return `${min}–${max}`;
  if (hasMin) return `${min}+`;
  if (hasMax) return `≤${max}`;
  return DASH;
}

// Meta encodes genders as [1] (male), [2] (female), [] / absent (all).
const GENDER_LABELS = { 1: 'Male', 2: 'Female' };
function resolveGender(targeting) {
  const list = asArray(targeting?.genders);
  if (!list.length) return 'All';
  const labels = list.map((g) => GENDER_LABELS[Number(g)] || nonEmpty(g)).filter(Boolean);
  return labels.length ? labels.join(', ') : 'All';
}

// Placements = publisher_platforms ∪ facebook_positions, in one cell. Meta
// treats them as one decision ("where does this run"), and splitting them
// across two columns puts half the answer off-screen.
function resolvePlacements(targeting) {
  const platforms = labelList(targeting?.publisher_platforms);
  const positions = [
    ...labelList(targeting?.facebook_positions),
    ...labelList(targeting?.instagram_positions),
    ...labelList(targeting?.messenger_positions),
    ...labelList(targeting?.audience_network_positions),
  ];
  const all = [...new Set([...platforms, ...positions])];
  const excluded = labelList(targeting?.excluded_positions);
  return {
    list: all,
    label: all.length
      ? (excluded.length ? `${all.join(' · ')} · excl. ${excluded.length}` : all.join(' · '))
      // No placement targeting IS a decision on Meta — it means Advantage+
      // placements, so say that rather than showing an em dash.
      : 'Advantage+ placements',
  };
}

// Custom audiences: the included list, with excluded ones counted alongside so
// a reviewer sees suppression exists without a column for it.
function resolveAudiences(targeting) {
  const included = [
    ...labelList(targeting?.custom_audiences),
    ...labelList(targeting?.lookalike_audiences),
  ];
  const excluded = labelList(targeting?.excluded_custom_audiences);
  return { included, excluded };
}

// Detailed targeting: `interests` plus anything inside Meta's flexible_spec
// (an OR-of-ANDs shape the LLM converter emits for layered targeting).
function resolveInterests(targeting) {
  const out = labelList(targeting?.interests);
  for (const spec of asArray(targeting?.flexible_spec)) {
    for (const key of ['interests', 'behaviors', 'demographics', 'life_events', 'industries']) {
      out.push(...labelList(spec?.[key]));
    }
  }
  return [...new Set(out)];
}

// ── Detection ────────────────────────────────────────────────────────

/**
 * True when this launcher result is a Facebook plan. Reads the platform field
 * first, then falls back to shape detection — the FB pipeline does NOT stamp
 * `platform` on parsed_response (only DV360 / Reddit / OpenAI / Google Ads /
 * MadHive do, see campaign_launcher/reasoning.py), so the shape test is the
 * path that actually fires in production, not a defensive afterthought.
 *
 * The test is deliberately narrow: an `ad_sets`-shaped array is unique to
 * Facebook among the platforms this grid renders (TTD has AdGroups, DV360
 * lineItems, Google Ads ad_groups / assets), and requiring the campaign
 * envelope alongside it keeps a stray key from claiming a foreign plan.
 */
export function isFbLauncherResult(result) {
  if (!result || typeof result !== 'object') return false;
  if (isFbPlatform(result.platform || result.Platform)) return true;
  if (!fbAdSetsOf(result).length) return false;
  // An ad_sets array plus either a campaign envelope or a Facebook-native
  // ad-set key. Both are present in every real plan; either alone is enough to
  // rule out a coincidental array on another platform's payload.
  if (Object.keys(fbCampaignOf(result)).length) return true;
  return fbAdSetsOf(result).some((a) => a && (
    a.ad_set_suffix != null
    || a.optimization_goal != null
    || a.billing_event != null
    || a.targeting_profiles != null
  ));
}

// Parse issues, if the launcher stamped any (same convention as TTD / gads).
function fbIssueLists(result) {
  return {
    errors: asArray(result?.templateErrors).filter(Boolean),
    warnings: asArray(result?.templateWarnings).filter(Boolean),
  };
}

// ── Budget ───────────────────────────────────────────────────────────

/**
 * An ad set's own budget. Meta ABO campaigns state it per ad set
 * (`budget` / `lifetime_budget` / `daily_budget`); CBO campaigns state it once
 * on the campaign and leave the ad sets empty, so the caller falls back to the
 * campaign's per-ad-set share.
 */
function adSetBudget(adSet) {
  const lifetime = fbMoney(adSet?.lifetime_budget ?? adSet?.budget);
  const daily = fbMoney(adSet?.daily_budget);
  return { lifetime, daily };
}

function campaignBudget(campaign) {
  const lifetime = fbMoney(campaign?.lifetime_budget ?? campaign?.budget);
  const daily = fbMoney(campaign?.daily_budget);
  return { lifetime, daily };
}

// ── Summary ──────────────────────────────────────────────────────────

/**
 * Headline numbers for the summary tiles / strip. Same contract as
 * summarizeTtdResult / summarizeGadsResult: consumers read platform, ioCount,
 * adGroupCount, totalBudget, flightStart/End, campaignName, audienceCount and
 * the issue counts.
 *
 * Facebook plans are currency-budgeted with no impression goal, so
 * totalImpressions / avgCpm are null by design — the same shape Google Ads
 * returns, which the summary pane already renders.
 */
export function summarizeFbResult(result) {
  if (!result) return null;
  const campaign = fbCampaignOf(result);
  const adSets = fbAdSetsOf(result);
  const camp = campaignBudget(campaign);

  // Prefer the sum of the ad sets (ABO) and fall back to the campaign's own
  // total (CBO) — summing both would double-count a CBO plan whose ad sets
  // echo the campaign figure.
  let adSetTotal = 0;
  let sawAdSetBudget = false;
  const starts = [];
  const ends = [];
  const audiences = new Set();
  for (const a of adSets) {
    const b = adSetBudget(a);
    if (b.lifetime != null) { adSetTotal += b.lifetime; sawAdSetBudget = true; }
    const s = fbYmd(a?.start_date) || fbYmd(campaign?.start_date);
    const e = fbYmd(a?.end_date) || fbYmd(campaign?.end_date);
    if (s) starts.push(s);
    if (e) ends.push(e);
    const t = fbTargetingOf(a, result);
    for (const aud of resolveAudiences(t).included) audiences.add(aud);
  }

  const issues = fbIssueLists(result);
  const packages = new Set(adSets.map((a) => nonEmpty(a?.package_name)).filter(Boolean));
  return {
    platform: FB_PLATFORM_LABEL,
    // Wording overrides for the shared summary tiles, which otherwise read
    // "Line items created / across N insertion orders".
    rowLabel: 'Ad sets parsed',
    parentLabel: 'campaign',
    ioCount: 1,
    adGroupCount: adSets.length,
    packageCount: packages.size,
    totalImpressions: null,
    avgCpm: null,
    totalBudget: sawAdSetBudget ? adSetTotal : camp.lifetime,
    estimatedBudget: null,
    flightStart: starts.length ? starts.sort()[0] : fbYmd(campaign?.start_date),
    flightEnd: ends.length ? ends.sort().slice(-1)[0] : fbYmd(campaign?.end_date),
    campaignName: nonEmpty(campaign?.campaign_name ?? campaign?.name),
    advertiserId: nonEmpty(result.ad_account_id || result.adAccountId) || null,
    audienceCount: audiences.size,
    issueCount: issues.errors.length + issues.warnings.length,
    errorCount: issues.errors.length,
    warningCount: issues.warnings.length,
  };
}

// ── Rows ─────────────────────────────────────────────────────────────

/**
 * One row per ad set. Row keys follow the shared review-grid vocabulary so
 * LaunchCampaignDetails' generic builders (format breakdown, QA tables,
 * flagged items, tactic rollup) work unchanged.
 *
 * Row ids are POSITIONAL (`fb-{adSetIdx}`): a Facebook plan carries no entity
 * ids before launch, exactly like Google Ads, so the index is the stable
 * address fbEdits resolves.
 */
export function buildFbReviewRows(result) {
  if (!result) return [];
  const campaign = fbCampaignOf(result);
  const adSets = fbAdSetsOf(result);
  const camp = campaignBudget(campaign);
  const objective = nonEmpty(campaign?.goal ?? campaign?.objective) || DASH;
  const campaignStart = fbYmd(campaign?.start_date);
  const campaignEnd = fbYmd(campaign?.end_date);
  // Per-ad-set share of the campaign budget, for the `campaignBudget` cell —
  // the divisor FB_SCALED_FIELDS scales back up on commit.
  const campaignShare = adSets.length && camp.lifetime != null
    ? camp.lifetime / adSets.length
    : null;

  const rows = adSets.map((adSet, i) => {
    const targeting = fbTargetingOf(adSet, result);
    const geo = resolveFbGeo(targeting);
    const budget = adSetBudget(adSet);
    const creatives = fbCreativesOf(adSet);
    const aud = resolveAudiences(targeting);
    const interests = resolveInterests(targeting);
    const placements = resolvePlacements(targeting);
    const zips = listSummary(geo.zips, 'zip');
    const audienceSummary = listSummary(aud.included, 'audience');
    const interestSummary = listSummary(interests, 'interest');
    const creativeSummary = listSummary(
      creatives.map((c) => nonEmpty(c?.creative_name ?? c?.name)),
      'creative',
    );
    const copySummary = listSummary(creatives.map((c) => nonEmpty(c?.ad_copy)), 'ad copy');
    const formats = [...new Set(creatives.map((c) => nonEmpty(c?.format)).filter(Boolean))];
    // The ad set's name as the launcher builds it: package + suffix is how
    // fb_campaign_launcher composes one, so a plan that states neither a `name`
    // nor both halves still gets something a reviewer can match in Ads Manager.
    const pkg = nonEmpty(adSet?.package_name);
    const suffix = nonEmpty(adSet?.ad_set_suffix);
    const name = nonEmpty(adSet?.name ?? adSet?.ad_set_name)
      || [pkg, suffix].filter(Boolean).join(' - ')
      || `Ad set ${i + 1}`;
    const start = fbYmd(adSet?.start_date) || campaignStart;
    const end = fbYmd(adSet?.end_date) || campaignEnd;
    const bidAmount = fbMoney(adSet?.bid_amount);
    // The landing page is per creative but effectively per ad set — show the
    // first, and flag when the ad set's creatives disagree so a reviewer knows
    // there is more behind the cell.
    const urls = [...new Set(creatives.map((c) => nonEmpty(c?.landing_page)).filter(Boolean))];

    return {
      rowId: `fb-${i}`,
      // Source platform ids for launched-edit targeting. Pre-launch launch
      // templates usually don't carry them; imported plans do.
      adset_id: nonEmpty(adSet?.id) || null,
      campaign_id: nonEmpty(adSet?.campaign_id ?? campaign?.id) || null,
      __refPath: `/ad_sets/${i}`,
      __adSetIdx: i,
      idx: i + 1,
      channel: FB_PLATFORM_LABEL,
      fmt: FB_FMT,
      name,
      market: nonEmpty(campaign?.campaign_name ?? campaign?.name) || DASH,
      package: pkg || DASH,
      category: nonEmpty(adSet?.ad_set_suffix) || DASH,
      tactic: nonEmpty(adSet?.tactic) || DASH,
      objective,
      attribution: nonEmpty(campaign?.attribution_setting) || DASH,
      start,
      end,
      // An ad set with its own dates runs its own flight; without them it
      // inherits the campaign's, which is what the launcher does too.
      flightType: (adSet?.start_date || adSet?.end_date) ? 'Own flight' : 'Inherits campaign',
      budget: budget.lifetime,
      dailyBudget: budget.daily,
      campaignBudget: campaignShare,
      // The divisor `campaignShare` came from — published so the cell editor
      // can disclose the campaign total before commit (see FB_SCALED_FIELDS).
      siblingCount: adSets.length || 1,
      pacing: budget.daily != null || camp.daily != null ? 'Daily budget' : 'Lifetime budget',
      entityStatus: nonEmpty(adSet?.status) || DASH,
      bidStrategy: nonEmpty(adSet?.bid_strategy ?? campaign?.bid_strategy) || DASH,
      bidFloor: bidAmount != null ? fmtCurrency(bidAmount) : DASH,
      optimizationGoal: nonEmpty(adSet?.optimization_goal) || DASH,
      billingEvent: nonEmpty(adSet?.billing_event) || DASH,
      geo: geo.geo,
      geoIncludeList: geo.geoIncludeList,
      geoExcludeList: geo.geoExcludeList,
      zipcodes: zips.label,
      zipcodeList: zips.list,
      audiences: audienceSummary.label,
      audienceList: audienceSummary.list,
      // Excluded audiences have no column of their own; surface the count on
      // the included cell's annotation-free tail so suppression is visible.
      excludedAudienceList: aud.excluded.length ? aud.excluded.join(' · ') : DASH,
      interests: interestSummary.label,
      interestList: interestSummary.list,
      interestIds: interests,
      age: resolveAge(targeting),
      gender: resolveGender(targeting),
      languages: labelList(targeting?.locales).join(', ') || DASH,
      languageList: labelList(targeting?.locales).join(' · ') || DASH,
      devices: labelList(targeting?.device_platforms).join(', ') || 'All devices',
      deviceList: labelList(targeting?.device_platforms).join(' · ') || DASH,
      placements: placements.label,
      placementList: placements.list.length ? placements.list.join(' · ') : DASH,
      creative: creativeSummary.label,
      creativeList: creativeSummary.list,
      creativeFormat: formats.length ? formats.join(' · ') : DASH,
      adCopy: copySummary.label,
      adCopyList: copySummary.list,
      clickURL: urls.length
        ? (urls.length === 1 ? urls[0] : `${urls[0]} · +${urls.length - 1} more`)
        : DASH,
      // Facebook plans carry no impression goal or CPM — the same nulls the
      // Google Ads rows publish, which the shared tiles already handle.
      imps: null,
      cpm: null,
      dailyCap: null,
      // Every remaining shared-vocabulary field the generic builders read,
      // defaulted so no consumer hits an undefined key.
      keywords: DASH,
      keywordList: DASH,
      keywordCount: 0,
      freqCap: DASH,
      environment: DASH,
      pmp: DASH,
      expansion: nonEmpty(targeting?.targeting_automation?.advantage_audience) != null
        ? 'Advantage+ audience'
        : DASH,
      viewability: DASH,
      brandSafety: DASH,
      pixel: DASH,
      kpi: DASH,
      kpiTarget: DASH,
      convWindow: nonEmpty(campaign?.attribution_setting) || DASH,
      bidGoal: nonEmpty(adSet?.optimization_goal) || DASH,
      annotations: {},
    };
  });

  return annotateFbRows(rows, result);
}

/**
 * Attach launcher-emitted templateErrors / templateWarnings to rows as the
 * shared annotations shape ({ field: { type, msg } }), so the Flagged Items tab
 * and the grid's issue highlighting light up.
 *
 * Facebook's own validator numbers its messages ("Ad Set 3: Missing …"), so
 * that prefix is matched first — it is the most reliable anchor available —
 * before falling back to a name match and finally to row 0.
 */
function annotateFbRows(rows, result) {
  const { errors, warnings } = fbIssueLists(result);
  if (!rows.length || (!errors.length && !warnings.length)) return rows;

  const attach = (row, field, type, msg) => {
    const existing = row.annotations[field];
    if (!existing) row.annotations[field] = { type, msg };
    else {
      existing.extra = existing.extra || [];
      existing.extra.push({ type, msg });
    }
  };
  const place = (msg, type) => {
    const s = String(msg);
    const numbered = /^ad set\s+(\d+)\s*:/i.exec(s.trim());
    if (numbered) {
      const row = rows[Number(numbered[1]) - 1];
      if (row) { attach(row, 'name', type, s); return; }
    }
    const lower = s.toLowerCase();
    const byRow = rows.find((r) => r.name && r.name !== DASH && lower.includes(String(r.name).toLowerCase()));
    if (byRow) { attach(byRow, 'name', type, s); return; }
    attach(rows[0], 'name', type, s);
  };
  for (const msg of errors) place(msg, 'm');
  for (const msg of warnings) place(msg, 'q');
  return rows;
}

/**
 * One row per PACKAGE for the summary pane's allocation table — the Facebook
 * counterpart of breakdownTtdByCampaign / breakdownDv360ByPackage.
 *
 * Facebook plans state a single campaign, so grouping by campaign would render
 * one meaningless row. The package IS the allocation unit here — it is what the
 * IO's line items map onto (platform_config's package_equivalent_level for fb
 * is the ad set, multiplied out of packages) — so the table answers "where did
 * the money go" the way the DV360 package table does.
 */
export function breakdownFbByPackage(result) {
  if (!result) return [];
  const rows = buildFbReviewRows(result);
  if (!rows.length) return [];
  const buckets = new Map();
  for (const r of rows) {
    const key = r.package !== DASH ? r.package : (r.market !== DASH ? r.market : 'Ad sets');
    if (!buckets.has(key)) {
      buckets.set(key, {
        key,
        formatCode: FB_FMT,
        packageName: key,
        categoryLabel: FB_PLATFORM_LABEL,
        lineItemCount: 0,
        impressions: 0,
        rate: null,
        budget: null,
        sizes: DASH,
        flightStart: null,
        flightEnd: null,
      });
    }
    const b = buckets.get(key);
    b.lineItemCount += 1;
    // Ad-set budgets (ABO) sum; a CBO plan has none, and the bucket keeps a
    // null budget rather than reporting $0 spend on a funded package.
    if (r.budget != null) b.budget = (b.budget || 0) + r.budget;
    if (r.start && (!b.flightStart || r.start < b.flightStart)) b.flightStart = r.start;
    if (r.end && (!b.flightEnd || r.end > b.flightEnd)) b.flightEnd = r.end;
    if (b.sizes === DASH && r.creativeFormat !== DASH) b.sizes = r.creativeFormat;
  }
  return [...buckets.values()];
}

/**
 * Campaign-level configuration card, mirroring extractTtdCampaignConfig /
 * extractGadsCampaignConfig so the summary pane's Configuration block has
 * Facebook-native content instead of a grid of em dashes.
 */
export function extractFbCampaignConfig(result) {
  if (!result) return {};
  const campaign = fbCampaignOf(result);
  const rows = buildFbReviewRows(result);
  const camp = campaignBudget(campaign);

  const geoSet = new Set();
  for (const r of rows) {
    if (r.geoIncludeList && r.geoIncludeList !== DASH) {
      for (const g of r.geoIncludeList.split(' · ')) geoSet.add(g);
    }
  }
  const geoSummary = geoSet.size === 0
    ? DASH
    : geoSet.size <= 4 ? [...geoSet].join(', ') : `${geoSet.size} locations`;

  const tactics = [...new Set(rows.map((r) => r.tactic).filter((t) => t && t !== DASH))];
  const formats = [...new Set(rows.flatMap((r) => (r.creativeFormat === DASH ? [] : r.creativeFormat.split(' · '))))];

  return {
    goal: nonEmpty(campaign?.goal ?? campaign?.objective) || DASH,
    kpi: rows.find((r) => r.optimizationGoal !== DASH)?.optimizationGoal || DASH,
    freqCap: DASH,
    advertiserId: nonEmpty(result.ad_account_id || result.adAccountId) || null,
    viewabilityTarget: DASH,
    conversionPixels: 0,
    standardExclusions: 0,
    geoSummary,
    tacticSummary: tactics.length ? tactics.join(', ') : DASH,
    formatSummary: formats.length ? formats.join(', ') : FB_FMT,
    budgetType: camp.daily != null ? 'Daily' : 'Lifetime',
    topPacing: camp.daily != null ? `${fmtCurrency(camp.daily)}/day` : (rows[0]?.pacing || DASH),
    topBid: rows.find((r) => r.bidFloor && r.bidFloor !== DASH)?.bidFloor || DASH,
    languageSummary: rows[0]?.languages || DASH,
    deviceSummary: rows[0]?.devices || 'All devices',
    partnerId: null,
  };
}
